<template>
  <!-- [guanxing patch] M-SW9 太阳系视图：vendor orrery.live 前端（用户明确要求
       一比一照抄、本地自用；源站交付的 app.js 为未压缩带注释源码，整站落盘
       public/solar/ 零外网）。iframe 同源嵌入：打开时用引擎时钟定位（?t=<J2000
       起算天数>，源站原生共享链接参数，打开即暂停），关闭时把太阳系仪停留时刻
       写回引擎 observer.utc；「飞出地球」衔接动画（星图广角拉起 + 地景淡出 +
       画布延迟淡入）保持不变。 -->
  <v-dialog fullscreen hide-overlay transition="v-dialog-fade-transition" v-model="$store.state.showSolarSystemDialog">
    <v-card v-if="$store.state.showSolarSystemDialog" class="solar-card" dark>
      <iframe v-if="frameSrc" ref="frame" class="solar-frame" :src="frameSrc" allowfullscreen></iframe>
      <!-- [guanxing patch] M-SW39（用户需求「还有 太阳系 也 做一个 俯视的 截图」）：
           俯视截图按钮——紧挨关闭按钮，点击后调 iframe 内同源桥 captureTopDown
           取回正上方俯瞰全景 PNG，弹出预览浮层。 -->
      <v-btn class="solar-shot" icon dark :loading="capturing" :disabled="!ready"
             :title="ready ? '俯视截图' : '贴图加载中…'"
             @click="capture"><v-icon>mdi-camera</v-icon></v-btn>
      <v-btn class="solar-close" icon dark @click="close"><v-icon>mdi-close</v-icon></v-btn>
      <!-- 预览浮层：先看图，再手动决定是否保存（与两张星图同一交互约定）。 -->
      <div v-if="shot || error" class="solar-preview" @click.self="dismiss">
        <div class="solar-preview-box">
          <img v-if="shot" :src="shot" class="solar-preview-img" alt="太阳系俯视图">
          <div v-if="error" class="solar-preview-err">{{ error }}</div>
          <div class="solar-preview-actions">
            <button v-if="shot" class="solar-pbtn solar-pbtn-download" type="button" @click="download">下载</button>
            <button class="solar-pbtn" type="button" @click="dismiss">关闭</button>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { appendFooter, footerUrl } from '@/assets/image-footer.js'
import { j2000DaysToUnixMs } from '@/assets/image-date.js'
import { saveImage } from '@/assets/image-save.js'
// [guanxing patch] M-SW9/M-SW9b：太阳系视图宿主。orrery.live 整站 vendor 在
// public/solar/（其 app.js 末尾追加了 __ORRERY_BRIDGE 同源桥），本组件只负责：
// ① 打开衔接动画——引擎 zoomTo 120° + 地景淡出 + iframe 延迟淡入；
// ② 时钟桥——打开 ?t= 定位（M-SW25 起固定为出生瞬间，见下），关闭把太阳系仪
//    停留时刻写回引擎（时间机器语义）。
const DEG = Math.PI / 180
// JD_J2000 = 2451545.0，MJD = JD - 2400000.5 ⇒ J2000 对应 MJD 51544.5。
const J2000_MJD = 51544.5
// [guanxing patch] M-SW25：Unix 纪元（1970-01-01T00:00Z）对应 MJD 40587，
// 用于把出生 iso（Date.parse → UTC ms）换算成 J2000 起算天数。
const UNIX_EPOCH_MJD = 40587

export default {
  data: function () {
    // [guanxing patch] M-SW39：capturing 取像中；shot 预览成图（dataURL）；
    // error 取像失败提示；ready 贴图就绪（未就绪时按钮置灰——贴图上载前取像
    // 只会得到黑球，慢机/软件渲染下可达十几秒）。
    return { frameSrc: null, capturing: false, shot: '', error: '', ready: false }
  },
  mounted: function () {
    // [guanxing patch] M-SW9d：iframe 内「← 返回观星」按钮经 postMessage 触发同一
    // close()（时间机器写回 + 视场/地景/流速恢复全走既有关闭链路）。
    this.$_onOrreryBack = (e) => { if (e.data && e.data.type === 'orrery-back') this.close() }
    window.addEventListener('message', this.$_onOrreryBack)
  },
  watch: {
    '$store.state.showSolarSystemDialog': function (open) {
      if (open) {
        // 打开前记下流速，关闭时恢复；对话框内的时间漫游不影响主视图常态。
        this.$_speedAtOpen = this.$stel ? this.$stel.core.time_speed : 1
        // [guanxing patch] M-SW9b 飞出地球过渡：星图广角拉起（fov→120°）+
        // 地景淡出，配合卡片透明与 iframe 淡入；关闭时对称恢复。
        if (this.$stel) {
          this.$_fovAtOpen = this.$stel.core.fov
          this.$stel.zoomTo(120 * DEG, 1.6)
          this.$_landAtOpen = this.$stel.core.landscapes.visible
          if (this.$_landAtOpen) this.$stel.core.landscapes.visible = false
        }
        // [guanxing patch] M-SW25（用户需求「你出生时的太阳系」）：星命弹窗是
        // 唯一入口，?t= 固定定位到出生瞬间——用 store 的 solarSystemBirthIso
        // （带时区偏移，Date.parse 得 UTC 瞬间）换算 J2000 起算天数；引擎时钟
        // 只作回退（无出生信息时理论不可达，防御性保留 M-SW9 原逻辑）。
        // 注意不能读引擎 utc：太阳系关闭链路会把停留时刻写回引擎，重开弹窗
        // 必须仍是出生瞬间。
        const birthIso = this.$store.state.solarSystemBirthIso
        const birthMs = birthIso ? Date.parse(birthIso) : NaN
        const days = Number.isFinite(birthMs)
          ? birthMs / 86400000 + UNIX_EPOCH_MJD - J2000_MJD
          : (this.$stel ? this.$stel.core.observer.utc - J2000_MJD : NaN)
        // [guanxing patch] 子路径部署（publicPath ≠ /）时不能用根绝对路径，
        // BASE_URL 会带上部署前缀（如 /guanxing/solar/）。
        this.frameSrc = Number.isFinite(days) ? process.env.BASE_URL + 'solar/?t=' + days : process.env.BASE_URL + 'solar/'
        // [guanxing patch] M-SW39b：轮询 iframe 的贴图就绪信号——贴图上载完成前
        // 取像只会拍到黑球，故就绪前把截图按钮置灰。
        this.ready = false
        this.startReadyPoll()
      } else {
        if (this.$stel && this.$_speedAtOpen !== undefined) {
          this.$stel.core.time_speed = this.$_speedAtOpen
        }
        // 关闭前（v-if 重绘前）同源读回太阳系仪停留时刻写回引擎。
        const frame = this.$refs.frame
        try {
          const bridge = frame && frame.contentWindow && frame.contentWindow.__ORRERY_BRIDGE
          const t = bridge && bridge.getTime()
          if (this.$stel && Number.isFinite(t)) this.$stel.core.observer.utc = J2000_MJD + t
        } catch (e) { /* iframe 未就绪/已销毁——跳过写回 */ }
        // [guanxing patch] M-SW9b：恢复打开前视场与地景（回程着陆）。
        if (this.$stel && this.$_fovAtOpen !== undefined) {
          this.$stel.zoomTo(this.$_fovAtOpen, 1.0)
        }
        if (this.$stel && this.$_landAtOpen === true) {
          this.$stel.core.landscapes.visible = true
        }
        // [guanxing patch] M-SW39：关对话框时清掉截图相关的临时状态。
        this.stopReadyPoll()
        this.capturing = false
        this.shot = ''
        this.error = ''
        this.ready = false
        this.frameSrc = null
      }
    }
  },
  beforeDestroy: function () {
    window.removeEventListener('message', this.$_onOrreryBack)
    this.stopReadyPoll()
    this.frameSrc = null
  },
  methods: {
    close: function () {
      this.$store.commit('setValue', { varName: 'showSolarSystemDialog', newValue: false })
    },
    // [guanxing patch] M-SW39b：贴图就绪轮询。桥缺失（iframe 还没起来/已销毁）
    // 视为未就绪；旧版桥没有 isReady 时按就绪处理（与版本容错）。
    bridge: function () {
      const frame = this.$refs.frame
      try {
        return (frame && frame.contentWindow && frame.contentWindow.__ORRERY_BRIDGE) || null
      } catch (e) { return null }
    },
    syncReady: function () {
      const b = this.bridge()
      if (!b) { this.ready = false; return }
      this.ready = (typeof b.isReady === 'function') ? !!b.isReady() : true
      if (this.ready) this.stopReadyPoll() // 就绪后不必再轮询
    },
    startReadyPoll: function () {
      this.stopReadyPoll()
      this.$_readyTimer = setInterval(this.syncReady, 700)
      // 立即试探一次，别等第一个 700ms
      this.$nextTick(this.syncReady)
    },
    stopReadyPoll: function () {
      if (this.$_readyTimer) { clearInterval(this.$_readyTimer); this.$_readyTimer = null }
    },
    // [guanxing patch] M-SW63：这一帧画面是哪一刻（Unix 毫秒），供右上角日期印章。
    // 时间源 = 太阳系桥 getTime()（J2000 起算天数，与打开时 ?t= 同一口径）——即
    // 用户当下在太阳系仪里停留的时刻，拖动时间后随之改变。取不到返回 NaN ⇒
    // 跳过印章。日期按设备本地时区换算（AskUserQuestion 定案）。
    stampMs: function () {
      const b = this.bridge()
      const t = b && typeof b.getTime === 'function' ? b.getTime() : NaN
      return j2000DaysToUnixMs(t)
    },
    // [guanxing patch] M-SW39：取一张正上方俯瞰全景图（1600×1600）。取像在
    // iframe 内同步完成（离屏渲染 + 读像素），不闪屏、不改用户当前视角与图层。
    // 主线程会被占住几百毫秒，故先让 loading 态渲染一帧再动手。
    capture: function () {
      if (this.capturing) return
      const bridge = this.bridge()
      if (!bridge || typeof bridge.captureTopDown !== 'function') {
        this.error = '太阳系视图还在加载，请稍候再试'
        return
      }
      // M-SW39b：贴图未就绪时取像只会得到黑球——当场拦下并提示，不产出坏图。
      if (typeof bridge.isReady === 'function' && !bridge.isReady()) {
        this.ready = false
        this.error = '贴图还在加载，请稍候再试'
        return
      }
      this.error = ''
      this.shot = ''
      this.capturing = true
      this.$nextTick(() => {
        setTimeout(async () => {
          try {
            const url = bridge.captureTopDown({ size: 1600 })
            if (!url || url.length < 100) throw new Error('取回的画面为空')
            // [guanxing patch] M-SW41：底部拼品牌页脚（banner 按截图宽度等比
            // 缩放；合成失败退回未加页脚的原图，不阻断取像链路）。
            // M-SW63：第三参 = 太阳系仪当下时刻，右上角盖公历日期（同一次
            // 画布合成，不新增编码）。
            this.shot = await appendFooter(url, footerUrl('star3.png'), this.stampMs())
          } catch (e) {
            console.error('[M-SW39] 太阳系俯视截图失败:', e)
            this.error = (e && e.message) ? e.message : String(e)
          } finally {
            this.capturing = false
          }
        }, 60)
      })
    },
    dismiss: function () {
      this.shot = ''
      this.error = ''
    },
    // [guanxing patch] M-SW55：保存统一走共享模块 image-save（旧版把数 MB 的
    // data:URL 塞进 <a href download>，iOS Safari 会当「打开图片」，用户只能长按
    // 存到相册）。iOS 弹系统分享面板；其它端 blob 下载。
    download: async function () {
      if (!this.shot) return
      const r = await saveImage(this.shot, '太阳系俯视图.png')
      if (r && r.blocked) {
        this.error = '系统把这次保存拦下了——请再点一次「下载」'
      }
    }
  }
}
</script>

<style scoped>
/* [guanxing patch] M-SW9：全屏对话框 = 满幅 iframe（vendor 的 orrery.live）
   + 右上角关闭钮；太阳系仪自带的控制面板/日期/流速/图层全在 iframe 内。 */
.solar-card {
  position: relative;
  /* [guanxing patch] M-SW9b：卡片透明——飞出地球动画的前段透出主星图
     （星图广角拉起 + 地景淡出），太阳系画布淡入后接管画面。 */
  background: transparent !important;
  overflow: hidden;
}
.solar-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  /* iframe 首帧绘制前垫源站同款底色避免白闪；淡入时序沿用 M-SW9b。 */
  background: #01020a;
  animation: solar-canvas-in 1.3s ease 0.35s backwards;
}
.solar-close {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 4;
  background: rgba(6, 6, 8, 0.6);
}
/* [guanxing patch] M-SW39：俯视截图按钮——紧挨关闭按钮左侧。 */
.solar-shot {
  position: absolute;
  top: 10px;
  right: 60px;
  z-index: 4;
  background: rgba(6, 6, 8, 0.6);
}
/* 预览浮层：铺满卡片，正中显示成图 + 下载/关闭。 */
.solar-preview {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 3, 8, 0.82);
  padding: 24px;
}
.solar-preview-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  max-width: 100%;
  max-height: 100%;
}
.solar-preview-img {
  max-width: min(78vw, 78vh);
  max-height: 78vh;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.65);
  background: #01020a;
}
.solar-preview-err {
  color: #ffb4a8;
  font-size: 14px;
}
.solar-preview-actions {
  display: flex;
  gap: 12px;
}
.solar-pbtn {
  padding: 8px 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(20, 22, 30, 0.9);
  color: #eee;
  font: 600 14px/1 -apple-system, "Segoe UI", system-ui, sans-serif;
  cursor: pointer;
}
/* [guanxing patch] M-SW48（用户需求「太阳系截图的按钮 改成灰色 不要 粉色」，
   AskUserQuestion 钉准目标=预览浮层「下载」按钮；原 rgba(80,120,200) 蓝底在
   用户手机上观感偏粉）：改中性灰底。第二轮用户仍反馈粉色——彻底去掉 accent
   语义类（不再走任何主题染色路径），换独立 .solar-pbtn-download 纯灰类。 */
.solar-pbtn-download {
  background: rgba(96, 102, 112, 0.95);
  border-color: rgba(255, 255, 255, 0.38);
  color: #fff;
}
@keyframes solar-canvas-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
